import type {
  NumberRegistration,
  NumberRegistrationsResult,
} from "~/types/numberRegistration";

/**
 * Perform an authenticated fetch request
 *
 * @param {string} url - The URL to fetch.
 * @param {string} method - The HTTP method to use (e.g., 'GET', 'POST').
 * @param {object} [body] - The request body, if any.
 * @param {boolean} [hasFiles=false] - Whether the request includes files.
 * @returns {Promise<any>} - A promise that resolves to the response of the fetch request.
 */
const authdFetch = async (
  url: string,
  method: string,
  body?: object,
  hasFiles = false
): Promise<any> => {
  const { $authdFetch } = useNuxtApp();

  // @ts-expect-error Need to use "type" here instead of "as unknown".
  return await ($authdFetch as unknown)(url, method, body, hasFiles);
};

/**
 * Get a list of number registrations by country ISO code
 *
 * @param {string} countryIso - The ISO code of the country.
 * @returns {Promise<NumberRegistration[]>} - A promise that resolves to an array of number registrations.
 */
export const getNumberRegistrationListByCountry = async (
  countryIso: string
): Promise<NumberRegistration[]> => {
  const runtimeConfig = useRuntimeConfig();
  const fbfNumbersApiUrlBase = runtimeConfig.public.fbfNumbersApiUrlBase;
  const url = `${fbfNumbersApiUrlBase}/number-registrations/?filter={"address.country":"${countryIso}"}`;
  const data = await authdFetch(url, "GET");
  return data.results as NumberRegistration[];
};

/**
 * Get a list of all registered numbers
 *
 * @returns {Promise<NumberRegistrationsResult>} - A promise that resolves to the result of number registrations.
 */
export const getNumberRegistrations = async (): Promise<NumberRegistrationsResult> => {
  const runtimeConfig = useRuntimeConfig();
  const fbfNumbersApiUrlBase = runtimeConfig.public.fbfNumbersApiUrlBase;
  const url = `${fbfNumbersApiUrlBase}/number-registrations`;
  const data = await authdFetch(url, "GET");
  return data as NumberRegistrationsResult;
};

/**
 * Request verification for a number registration
 *
 * @param {string} numberRegistrationId - The ID of the number registration.
 * @returns {Promise<any>} - A promise that resolves to the response of the verification request.
 */
export const requestNumberRegistrationVerification = async (
  numberRegistrationId: string
): Promise<any> => {
  const runtimeConfig = useRuntimeConfig();
  const fbfNumbersApiUrlBase = runtimeConfig.public.fbfNumbersApiUrlBase;
  const url = `${fbfNumbersApiUrlBase}/number-registrations/${numberRegistrationId}/request-verification`;
  const data = await authdFetch(url, "POST");
  return data;
};

/**
 * Upload a registration document
 *
 * @param {File | null} file - The file to upload.
 * @param {string} type - The type of the document.
 * @param {string} [subType] - The subtype of the document, if any.
 * @returns {Promise<{ fileName: string, message: string }>} - A promise that resolves to the response of the upload.
 */
export const uploadRegistrationDoc = async (
  file: File | null,
  type: string,
  subType?: string
): Promise<{ fileName: string, message: string }> => {
  const runtimeConfig = useRuntimeConfig();
  const fbfNumbersApiUrlBase = runtimeConfig.public.fbfNumbersApiUrlBase;
  const url = `${fbfNumbersApiUrlBase}/number-registrations/upload-document`;

  const formData = new FormData();
  if (file) {
    formData.append("docType", type);
    formData.append("file", file, file.name);

    if (subType) {
      formData.append("docSubType", subType);
    }
  }

  return await authdFetch(url, "POST", formData, true) as { fileName: string, message: string };
};

/**
 * Create a new number registration
 *
 * @param {object} formData - The form data for the new number registration.
 * @returns {Promise<any>} - A promise that resolves to the response of the creation request.
 */
export const createNumberRegistration = async (formData: object): Promise<any> => {
  const runtimeConfig = useRuntimeConfig();
  const fbfNumbersApiUrlBase = runtimeConfig.public.fbfNumbersApiUrlBase;
  const url = `${fbfNumbersApiUrlBase}/number-registrations`;

  return await authdFetch(url, "POST", formData);
};

/**
 * Get a number registration by its ID
 *
 * @param {string} numberRegistrationId - The ID of the number registration.
 * @returns {Promise<NumberRegistration>} - A promise that resolves to the number registration.
 */
export const getNumberRegistrationById = async (numberRegistrationId: string): Promise<NumberRegistration> => {
  const runtimeConfig = useRuntimeConfig();
  const fbfNumbersApiUrlBase = runtimeConfig.public.fbfNumbersApiUrlBase;
  const url = `${fbfNumbersApiUrlBase}/number-registrations/${numberRegistrationId}`;
  return await authdFetch(url, "GET") as NumberRegistration;
};

/**
 * Delete a number registration by its ID
 *
 * @param {string} numberRegistrationId - The ID of the number registration.
 * @returns {Promise<any>} - A promise that resolves to the response of the deletion request.
 */
export const deleteNumberRegistration = async (numberRegistrationId: string): Promise<any> => {
  const runtimeConfig = useRuntimeConfig();
  const fbfNumbersApiUrlBase = runtimeConfig.public.fbfNumbersApiUrlBase;
  const url = `${fbfNumbersApiUrlBase}/number-registrations/${numberRegistrationId}`;

  return await authdFetch(url, "DELETE");
};

/**
 * Update a number registration by its ID
 *
 * @param {string} numberRegistrationId - The ID of the number registration.
 * @param {object} formData - The form data for the update.
 * @returns {Promise<any>} - A promise that resolves to the response of the update request.
 */
export const updateNumberRegistration = async (
  numberRegistrationId: string,
  formData: object
): Promise<any> => {
  const runtimeConfig = useRuntimeConfig();
  const fbfNumbersApiUrlBase = runtimeConfig.public.fbfNumbersApiUrlBase;
  const url = `${fbfNumbersApiUrlBase}/number-registrations/${numberRegistrationId}`;
  
  return await authdFetch(url, "PUT", formData);
};

/**
 * Get linked numbers for a number registration by its ID
 *
 * @param {string} numberRegistrationId - The ID of the number registration.
 * @returns {Promise<string[]>} - A promise that resolves to an array of linked numbers.
 */
export const getLinkedNumbers = async (numberRegistrationId: string): Promise<string[]> => {
  const runtimeConfig = useRuntimeConfig();
  const fbfNumbersApiUrlBase = runtimeConfig.public.fbfNumbersApiUrlBase;
  const url = `${fbfNumbersApiUrlBase}/number-registrations/${numberRegistrationId}/linked-numbers`;
  return await authdFetch(url, "GET") as string[];
};